import { useState } from "react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Sparkle, Lightning, Warning, Clock, House, ArrowClockwise, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const URGENCY_STYLES = {
  critical: { bg: "bg-red-100 dark:bg-red-900/30", text: "text-red-700 dark:text-red-400", badge: "bg-red-100 text-red-700" },
  high: { bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-400", badge: "bg-amber-100 text-amber-700" },
  medium: { bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400", badge: "bg-blue-100 text-blue-700" },
  low: { bg: "bg-emerald-100 dark:bg-emerald-900/30", text: "text-emerald-700 dark:text-emerald-400", badge: "bg-emerald-100 text-emerald-700" },
};

export default function PredictiveMaintenancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const analyse = async () => {
    setLoading(true);
    try {
      const { data: result } = await API.post("/ai/predictive-maintenance");
      setData(result);
      if (result.predictions?.length) {
        toast.success(`${result.predictions.length} prediction(s) generated`);
      } else {
        toast.info(result.message || "No patterns detected yet. More job history needed.");
      }
    } catch {
      toast.error("Analysis failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="predictive-maintenance-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <Sparkle size={18} className="text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Predictive Maintenance</h1>
            <p className="text-xs text-muted-foreground">AI analyses your job history to predict upcoming maintenance needs</p>
          </div>
        </div>
        <Button
          onClick={analyse}
          disabled={loading}
          className="bg-amber-600 hover:bg-amber-700"
          data-testid="run-analysis-btn"
        >
          {loading ? (
            <span className="flex items-center gap-2"><Lightning size={14} className="animate-pulse" /> Analysing...</span>
          ) : (
            <span className="flex items-center gap-2"><ArrowClockwise size={14} /> {data ? "Re-analyse" : "Run Analysis"}</span>
          )}
        </Button>
      </div>

      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="analysis-summary">
          <div className="border border-border rounded-lg p-4 bg-card">
            <p className="text-2xl font-bold font-[Chivo]">{data.summary.properties_analysed}</p>
            <p className="text-xs text-muted-foreground">Properties Analysed</p>
          </div>
          <div className="border border-border rounded-lg p-4 bg-card">
            <p className="text-2xl font-bold font-[Chivo]">{data.summary.predictions_generated}</p>
            <p className="text-xs text-muted-foreground">Predictions Generated</p>
          </div>
          <div className="border border-border rounded-lg p-4 bg-card">
            <p className="text-2xl font-bold font-[Chivo] text-red-600">{data.summary.high_priority}</p>
            <p className="text-xs text-muted-foreground">High Priority</p>
          </div>
        </div>
      )}

      {/* Predictions List */}
      {data?.predictions?.length > 0 ? (
        <div className="space-y-3" data-testid="predictions-list">
          {data.predictions.map((p, i) => {
            const style = URGENCY_STYLES[p.urgency] || URGENCY_STYLES.medium;
            return (
              <div key={i} className={`border border-border rounded-lg p-4 ${style.bg}`} data-testid={`prediction-${i}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <House size={16} className={style.text} weight="duotone" />
                    <span className="text-sm font-semibold">{p.property}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[9px] ${style.badge}`}>{p.urgency}</Badge>
                    {p.confidence && (
                      <Badge variant="outline" className="text-[9px]">{Math.round(p.confidence * 100)}% confidence</Badge>
                    )}
                  </div>
                </div>
                <p className="text-sm mb-2">{p.prediction}</p>
                <p className="text-xs text-muted-foreground mb-2">{p.reasoning}</p>
                <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
                  {p.category && (
                    <span className="flex items-center gap-1"><ShieldCheck size={10} /> {p.category}</span>
                  )}
                  {p.suggested_timeframe && (
                    <span className="flex items-center gap-1"><Clock size={10} /> {p.suggested_timeframe}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : data && !loading ? (
        <div className="text-center py-16 border border-dashed border-border rounded-md" data-testid="no-predictions">
          <Warning size={32} className="mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground mb-1">Not enough data for predictions yet</p>
          <p className="text-xs text-muted-foreground">Predictions improve as more jobs are completed across your properties. Try again when you have more job history.</p>
        </div>
      ) : !loading ? (
        <div className="text-center py-16 border border-dashed border-border rounded-md">
          <Sparkle size={32} className="mx-auto text-amber-500 mb-3" weight="duotone" />
          <p className="text-muted-foreground mb-1">AI-Powered Predictive Maintenance</p>
          <p className="text-xs text-muted-foreground mb-4">Click "Run Analysis" to scan your property portfolio for maintenance patterns and upcoming needs.</p>
          <Button variant="outline" size="sm" onClick={analyse} data-testid="run-analysis-empty-btn">Run Analysis</Button>
        </div>
      ) : null}
    </div>
  );
}
